import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { saveData } from 'services/api/saveData';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import { TIER_RATE_LIMITS } from './ApiTierOrderModal';
import '../../../style/scss/billing.scss';

interface ApiTierSetModalProps {
    tokenId: number;
    currentTier: string;
    onSuccess?: () => void;
};

// The selectable api tiers in order
const API_TIERS = ['free', 'standard', 'extended'];

// Modal for superusers and staff to set the api tier of a key directly without an order or payment
const ApiTierSetModal: React.FC<ApiTierSetModalProps> = ({ tokenId, currentTier, onSuccess }) => {
    const { t } = useTranslation();
    const { closeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [selectedTier, setSelectedTier] = useState<string>(currentTier);
    const [buttonLoader, setButtonLoader] = useState<boolean>(false);

    // Set the selected tier on the api key in the backend
    const handleSetTier = async () => {
        setButtonLoader(true);

        // Save the tier and show a success alert, errors show a danger alert
        try {
            await saveData({
                apiUrl: `set_api_token_tier/${tokenId}`,
                method: 'post',
                data: { tier: selectedTier }
            });
            setFloatingAlert({ type: 'success', message: 'api_key.general.tier_set_success_message' });
            onSuccess?.();
            closeModal();
        } catch (error) {
            console.error('Error on setting the api tier', error);
            setFloatingAlert({ type: 'danger', message: 'alert.general_save_error_message' });
        } finally {
            setButtonLoader(false);
        }
    };

    return (
        <div className='module-order-modal api-tier-order-modal'>

            {/* Show the modal header with a short explanation */}
            <h3>{t('api_key.general.tier_set_modal_title')}</h3>
            <p>{t('api_key.general.tier_set_modal_description')}</p>

            {/* Show every tier as selectable card, the current tier gets a badge */}
            <div className='api-tier-cards'>
                {API_TIERS.map((tier) => (
                    <div
                        key={tier}
                        className={`api-tier-card ${selectedTier === tier ? 'selected' : ''}`}
                        onClick={() => setSelectedTier(tier)}
                    >
                        <div className='tier-header'>
                            <h5>{t(`api_key.tier.${tier}`)}</h5>
                            {tier === currentTier && (
                                <span className='current-tier-badge'>{t('api_key.general.tier_current_label')}</span>
                            )}
                        </div>
                        <p className='tier-use-case'>{t(`api_key.tier_use_case.${tier}`)}</p>
                        <ul className='rate-limit-list'>
                            <li>{t('api_key.general.list_requests_label')}: {TIER_RATE_LIMITS[tier]?.list}</li>
                            <li>{t('api_key.general.detail_requests_label')}: {TIER_RATE_LIMITS[tier]?.detail}</li>
                            <li>{t('api_key.general.write_requests_label')}: {TIER_RATE_LIMITS[tier]?.write}</li>
                            <li>{t('api_key.general.daily_requests_label')}: {TIER_RATE_LIMITS[tier]?.daily}</li>
                        </ul>
                    </div>
                ))}
            </div>

            {/* The modal buttons */}
            <div className='button-row'>
                <div className='buttons-right'>
                    <SecondaryButton
                        type='button'
                        label='general.cancel'
                        size='small'
                        onClick={() => closeModal()}
                    />
                    <PrimaryButton
                        type='button'
                        label='api_key.general.tier_set_label'
                        size='small'
                        loading={buttonLoader}
                        disabled={{ disabled: selectedTier === currentTier }}
                        onClick={handleSetTier}
                    />
                </div>
            </div>
        </div>
    );
};

export default ApiTierSetModal;
